// src/features/settings/SecuritySettings.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Divider, CircularProgress, Alert } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { resetPasswordWithOld, clearAuthStatus } from '../../features/auth/authSlice';
import { validatePassword } from '../../utils/validation';
import PasswordField from '../../components/common/PasswordField';
import DeleteAccountDialog from './DeleteAccountDialog'; // Import the new dialog
import { useTranslation } from 'react-i18next'; // Import i18n hook

const SecuritySettings: React.FC = () => {
    // Initialize i18n translation
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { status, message, error } = useAppSelector(state => state.auth);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
    const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
    const [localError, setLocalError] = useState('');
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentPasswordTouched, setCurrentPasswordTouched] = useState(false);
    const [newPasswordTouched, setNewPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

    // Clear auth status on component mount
    useEffect(() => {
        dispatch(clearAuthStatus());
    }, [dispatch]);

    // Validate inputs only if the field has been touched
    useEffect(() => {
        // Validate current password
        if (currentPasswordTouched) {
            if (!currentPassword.trim()) {
                setCurrentPasswordError(t('current_password_required'));
            } else {
                setCurrentPasswordError(null);
            }
        }

        // Validate new password
        if (newPasswordTouched) {
            const passwordError = validatePassword(newPassword);
            if (!newPassword.trim()) {
                setNewPasswordError(t('new_password_required'));
            } else if (passwordError) {
                setNewPasswordError(passwordError);
            } else {
                setNewPasswordError(null);
            }
        }

        // Validate confirm password
        if (confirmPasswordTouched) {
            if (!confirmPassword.trim()) {
                setConfirmPasswordError(t('confirm_password_required'));
            } else if (newPassword !== confirmPassword) {
                setConfirmPasswordError(t('passwords_do_not_match'));
            } else {
                setConfirmPasswordError(null);
            }
        }
    }, [
        currentPassword,
        newPassword,
        confirmPassword,
        currentPasswordTouched,
        newPasswordTouched,
        confirmPasswordTouched,
        t
    ]);

    // Handle password change submission
    const handlePasswordChange = (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError('');
        dispatch(clearAuthStatus());

        // Mark all fields as touched to trigger validation
        setCurrentPasswordTouched(true);
        setNewPasswordTouched(true);
        setConfirmPasswordTouched(true);

        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setLocalError(passwordError);
            return;
        }

        if (newPassword !== confirmPassword) {
            setLocalError(t('passwords_do_not_match'));
            return;
        }
        dispatch(resetPasswordWithOld({ currentPassword, newPassword }));
    };

    // Determine if the Update Password button should be disabled
    const isButtonDisabled = status.auth === 'loading' ||
        (currentPasswordTouched && !!currentPasswordError) ||
        (newPasswordTouched && !!newPasswordError) ||
        (confirmPasswordTouched && !!confirmPasswordError) ||
        !currentPassword.trim() ||
        !newPassword.trim() ||
        !confirmPassword.trim();

    return (
        <Box>
            <Typography variant="h4" gutterBottom>{t('security_settings')}</Typography>

            {/* Change Password Section */}
            <Box component="form" onSubmit={handlePasswordChange} sx={{ py: 3 }}>
                <Typography variant="h6" gutterBottom>{t('change_password')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('password_requirements')}
                </Typography>
                {localError && <Alert severity="warning" sx={{ mb: 2 }}>{localError}</Alert>}
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={1} direction="column">
                    <Grid item {...{} as any}>
                        <PasswordField
                            label={t('current_password')}
                            value={currentPassword}
                            onChange={e => {
                                setCurrentPassword(e.target.value);
                                setCurrentPasswordTouched(true);
                            }}
                            sx={{ maxWidth: 400 }}
                            error={currentPasswordTouched && !!currentPasswordError}
                            helperText={currentPasswordTouched ? currentPasswordError : null}
                            required
                        />
                    </Grid>
                    <Grid item {...{} as any}>
                        <PasswordField
                            label={t('new_password')}
                            value={newPassword}
                            onChange={e => {
                                setNewPassword(e.target.value);
                                setNewPasswordTouched(true);
                            }}
                            sx={{ maxWidth: 400 }}
                            error={newPasswordTouched && !!newPasswordError}
                            helperText={newPasswordTouched ? newPasswordError : null}
                            required
                        />
                    </Grid>
                    <Grid item {...{} as any}>
                        <PasswordField
                            label={t('confirm_new_password')}
                            value={confirmPassword}
                            onChange={e => {
                                setConfirmPassword(e.target.value);
                                setConfirmPasswordTouched(true);
                            }}
                            sx={{ maxWidth: 400 }}
                            error={confirmPasswordTouched && !!confirmPasswordError}
                            helperText={confirmPasswordTouched ? confirmPasswordError : null}
                            required
                        />
                    </Grid>
                    <Grid item {...{} as any}>
                        <Box sx={{ py: 1 }}/>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isButtonDisabled}
                            sx={{
                                // Apply LoginPage button styles
                                bgcolor: 'inverse.main',
                                color: 'inverse.contrastText',
                                '&:hover': {
                                    bgcolor: 'inverse.hover.main',
                                },
                            }}
                        >
                            {status.auth === 'loading' ? <CircularProgress size={24} color="inherit" /> : t('update_password')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Delete Account Section */}
            <Box>
                <Typography variant="h6" gutterBottom color="error">{t('delete_account')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('delete_account_warning')}
                </Typography>
                <Box sx={{ py: 1 }}/>
                <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
                    {t('delete_account')}
                </Button>
            </Box>

            <DeleteAccountDialog
                open={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            />
        </Box>
    );
};

export default SecuritySettings;